import React from "react";
import { cilInfo } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import { CCard, CCardBody, CCol, CRow, CTooltip } from "@coreui/react";
import { ColoredValue } from "@src/components/ColoredValue";
import type * as T from "@src/types";
import { TVLight } from "@tradingdroid/frontends-components";

import { preparePropsForLightChart } from "../TradingView/utils/tvLight";
import { MonthlyPerformanceGrid } from "./MonthlyPerformanceGrid";
import { AccountingCoverageNotice } from "./AccountingCoverageNotice";

interface Props {
  runResult: T.RunResult;
  coverageMetrics?: T.TDT.BacktestMetrics | null;
}

export const RunPerformance: React.FC<Props> = ({
  runResult,
  coverageMetrics = runResult.metrics,
}) => {
  const { metrics, initial_capital: initialCapital, series, positions } = runResult;
  const coverage = (
    <AccountingCoverageNotice
      metrics={coverageMetrics}
      positions={positions || []}
      quoteCurrency={runResult.symbol?.quote ?? null}
    />
  );

  if (!metrics || (metrics?.total_positions || 0) === 0) {
    return (
      <div className="p-3">
        {coverage}
        <h6>Performance Metrics</h6>
        <p>No performance data found</p>
      </div>
    );
  }

  return (
    <div id="RunPerformance">
      {coverage}
      {/* Performance Chart */}
      <CCard className="PerformanceChartCard border-0">
        <CCardBody className="p-2">
          <h6 className="mb-2">Performance Metrics</h6>
          <TVLight {...preparePropsForLightChart(series, {}, 250)} />
        </CCardBody>
      </CCard>

      <hr className="my-3" />

      {/* Monthly Performance Grid */}
      <CCard className="MonthlyPerformanceCard border-0">
        <CCardBody className="p-2">
          <MonthlyPerformanceGrid positions={positions || []} initialCapital={initialCapital} />
        </CCardBody>
      </CCard>

      <hr className="my-3" />

      {/* Detailed Metrics Grid */}
      <CCard className="DetailedMetricsCard border-0">
        <CCardBody className="p-2">
          <h6 className="mb-2">Detailed Statistics</h6>
          <CRow>
            {/* Column 1: Basic Trade Statistics */}
            <CCol className="col-trade-stats">
              <h6 className="mb-3">Trade Stats</h6>

              <div className="mb-3 metric-total-trades">
                <small className="text-muted">
                  Total Trades
                  <CTooltip content="Total number of positions opened during the backtest">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">{metrics.total_positions}</div>
              </div>

              <div className="mb-3 metric-win-loss">
                <small className="text-muted">
                  Win / Loss
                  <CTooltip content="Number of profitable vs unprofitable trades">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  <ColoredValue value={metrics.win_signals || 0} forceColor="win" type="flat" />
                  {" / "}
                  <ColoredValue value={metrics.loss_signals || 0} forceColor="loss" type="flat" />
                </div>
              </div>

              <div className="mb-3 metric-win-rate">
                <small className="text-muted">
                  Win Rate
                  <CTooltip content="Percentage of trades that were profitable">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  <ColoredValue
                    value={(metrics.win_rate || 0) * 100}
                    threshold={50}
                    type="percentage"
                    decimals={1}
                  />
                </div>
              </div>
              <div className="metric-profit-factor">
                <small className="text-muted">
                  Profit Factor
                  <CTooltip content="Gross profits divided by gross losses">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  <ColoredValue
                    value={metrics.profit_factor || 0}
                    threshold={1}
                    type="number"
                    decimals={2}
                  />
                </div>
              </div>
            </CCol>

            {/* Column 2: Returns */}
            <CCol className="col-returns">
              <h6 className="mb-3">Returns</h6>

              <div className="mb-3 metric-net-profit">
                <small className="text-muted">
                  Net Profit
                  <CTooltip content="Total dollar gain/loss">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  <ColoredValue
                    value={metrics.cumulative_returns || 0}
                    threshold={0}
                    type="currency"
                  />
                </div>
              </div>

              <div className="mb-3 metric-return-pct">
                <small className="text-muted">
                  Return %
                  <CTooltip content="Percentage return on initial capital">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  <ColoredValue
                    value={metrics.cumulative_returns_pct || 0}
                    threshold={0}
                    type="percentage"
                    decimals={2}
                  />
                </div>
              </div>

              <div className="mb-3 metric-initial-capital">
                <small className="text-muted">
                  Initial Capital
                  <CTooltip content="Starting capital amount for the backtest">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  {(metrics.initial_capital ?? initialCapital) ? (
                    <ColoredValue
                      value={metrics.initial_capital ?? initialCapital}
                      type="currency"
                      decimals={2}
                    />
                  ) : (
                    "-"
                  )}
                </div>
              </div>

              <div className="metric-current-capital">
                <small className="text-muted">
                  Current Capital
                  <CTooltip content="Final capital after all trades including profit/loss">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  <ColoredValue
                    value={
                      metrics.final_capital ||
                      (metrics.initial_capital || initialCapital) +
                        (metrics.cumulative_returns || 0)
                    }
                    threshold={metrics.initial_capital || initialCapital}
                    type="currency"
                  />
                </div>
              </div>
            </CCol>

            {/* Column 3: Risk Metrics */}
            <CCol className="col-risk">
              <h6 className="mb-3">Risk</h6>

              <div className="mb-3 metric-max-drawdown">
                <small className="text-muted">
                  Max Drawdown
                  <CTooltip content="Largest peak-to-trough decline">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  <ColoredValue
                    value={metrics.max_drawdown_pct || 0}
                    forceColor="loss"
                    type="percentage"
                    decimals={2}
                  />
                </div>
              </div>

              <div className="mb-3 metric-wl-ratio">
                <small className="text-muted">
                  W/L Ratio
                  <CTooltip content="Average win size divided by average loss size">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  <ColoredValue
                    value={metrics.win_loss_ratio || 0}
                    threshold={1}
                    type="number"
                    decimals={2}
                  />
                </div>
              </div>
            </CCol>

            {/* Column 4: Trade Analysis */}
            <CCol className="col-trade-analysis">
              <h6 className="mb-3">Trade Analysis</h6>

              <div className="mb-3 metric-avg-win-amount">
                <small className="text-muted">
                  Avg Win $
                  <CTooltip content="Average profit amount on winning trades">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  <ColoredValue value={metrics.avg_win_amount ?? 0} threshold={0} type="currency" />
                </div>
              </div>

              <div className="mb-3 metric-avg-loss-amount">
                <small className="text-muted">
                  Avg Loss $
                  <CTooltip content="Average loss amount on losing trades">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  <ColoredValue
                    value={Math.abs(metrics.avg_loss_amount ?? 0)}
                    forceColor="loss"
                    type="currency"
                  />
                </div>
              </div>

              <div className="mb-3 metric-avg-win-pct">
                <small className="text-muted">
                  Avg Win %
                  <CTooltip content="Average percentage gain on winning trades">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  {metrics.avg_win_pct != null ? (
                    <ColoredValue
                      value={metrics.avg_win_pct}
                      threshold={0}
                      type="percentage"
                      decimals={2}
                    />
                  ) : (
                    "-"
                  )}
                </div>
              </div>

              <div className="metric-avg-loss-pct">
                <small className="text-muted">
                  Avg Loss %
                  <CTooltip content="Average percentage loss on losing trades">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  {metrics.avg_loss_pct != null ? (
                    <ColoredValue
                      value={Math.abs(metrics.avg_loss_pct)}
                      forceColor="loss"
                      type="percentage"
                      decimals={2}
                    />
                  ) : (
                    "-"
                  )}
                </div>
              </div>
            </CCol>

            {/* Column 5: Advanced Metrics */}
            <CCol className="col-advanced">
              <h6 className="mb-3">Advanced</h6>

              <div className="mb-3 metric-sharpe-ratio">
                <small className="text-muted">
                  Sharpe Ratio
                  <CTooltip content="Return per unit of risk (higher is better)">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  <ColoredValue
                    value={metrics.sharpe_ratio ?? 0}
                    threshold={1}
                    type="number"
                    decimals={2}
                  />
                </div>
              </div>

              <div className="mb-3 metric-sortino-ratio">
                <small className="text-muted">
                  Sortino Ratio
                  <CTooltip content="Return per unit of downside risk">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  <ColoredValue
                    value={metrics.sortino_ratio ?? 0}
                    threshold={1}
                    type="number"
                    decimals={2}
                  />
                </div>
              </div>

              <div className="mb-3 metric-calmar-ratio">
                <small className="text-muted">
                  Calmar Ratio
                  <CTooltip content="Return relative to maximum drawdown">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  <ColoredValue
                    value={metrics.calmar_ratio ?? 0}
                    threshold={1}
                    type="number"
                    decimals={2}
                  />
                </div>
              </div>

              <div className="metric-apy">
                <small className="text-muted">
                  APY
                  <CTooltip content="Annualized Percentage Yield - projected yearly return rate">
                    <CIcon
                      className="ms-1 text-info"
                      icon={cilInfo}
                      size="sm"
                      style={{ cursor: "help" }}
                    />
                  </CTooltip>
                </small>
                <div className="fw-bold">
                  <ColoredValue
                    value={metrics.annualized_return_pct ?? 0}
                    threshold={0}
                    type="percentage"
                    decimals={2}
                  />
                </div>
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
    </div>
  );
};
